import {Component, Inject } from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {FormArray, FormControl, FormGroup, Validators,
    AbstractControl, ValidationErrors, ValidatorFn} from '@angular/forms';
import { WindowService } from '../../../../services/window.service';

export interface IEnrollCodeEndpoint {
    licenseId: number;
    biosSerial: string;
    endpointName: string;
    diskSerial: string;
    pwMin: number;
}

export type EnrollCodeType = 'pba' | 'fde';

export interface IEnrollCodesDialogOptions {
    type: EnrollCodeType;
    title: string;
    endpoints: IEnrollCodeEndpoint[];
}

export interface IEnrollCodeRow {
    licenseId: number;
    biosSerial: string;
    endpointName: string;
    diskSerial: string;
    pwMin: number;
    enroll_code_id: string;
}

export interface IEnrollCodesDialogResult {
    type: EnrollCodeType;
    usage_count: number;
    expires_dttm: string;
    codes: IEnrollCodeRow[];
}

export interface IFailedEnrollCode {
    endpointId?: number;
    endpointName?: string;
    product: string;
    diskSerial: string;
    message: string;
}

export interface IAddEnrollCodesResponse {
    success: boolean;
    endpointName: string,
    partial_success: boolean;
    message?: string;
    codes: IAddedEnrollCodeResponse[];
    failed_codes: IFailedEnrollCode[];
}

export interface IAddedEnrollCode
    extends IAddedEnrollCodeResponse {
    code: string;
}

export interface IAddedEnrollCodeResponse {
    endpointId: number;
    endpointName: string,
    product: 'PBA' | 'FDE';
    diskSerial: string;
    usageLimit: number;
    expiration: string;
}

@Component({
    selector: 'app-enroll-codes-dialog',
    templateUrl: './enroll-codes-dialog.component.html',
    styleUrl: './enroll-codes-dialog.component.scss',
    standalone: false
})
export class EnrollCodesDialogComponent {
    displayedColumns = ['endpoint', 'codeId', 'generate'];

    form = new FormGroup({
        usage_count: new FormControl(5, [
            Validators.required,
            control => {
                const value = control.value;
                return value === -1 || value >= 1 ? null : {invalidUsageCount: true};
            }
        ]),
        unlimited_usage: new FormControl(false),
        expires_dttm: new FormControl<Date | string | null>(
            this.getDefaultExpirationDate(),
            [
                Validators.required,
                this.expirationValidator(),
            ]
        ),
        perpetual: new FormControl(false),
        rows: new FormArray<FormGroup>([]),
    });

    readonly codeMinimumMin = 8;
    readonly codeMinimumMax = 128;

    constructor(
        public readonly dialogRef: MatDialogRef<EnrollCodesDialogComponent>,
        private windowService: WindowService,
        @Inject(MAT_DIALOG_DATA) public options: IEnrollCodesDialogOptions
    ) {
        options.endpoints.forEach(endpoint => {
            this.rows.push(new FormGroup({
                licenseId: new FormControl(endpoint.licenseId),
                biosSerial: new FormControl(endpoint.biosSerial),
                endpointName: new FormControl(endpoint.endpointName),
                diskSerial: new FormControl(endpoint.diskSerial),
                pwMin: new FormControl(endpoint.pwMin),
                enroll_code_id: new FormControl('', Validators.required),
            }));
        });
    }

    ngOnInit(): void {
        this.rows.controls.forEach((_, index) => {
            this.generateCode(index);
        });

        this.form.controls.unlimited_usage.valueChanges.subscribe(
            unlimited => {
                const usageControl = this.form.controls.usage_count;

                if (unlimited) {
                    usageControl.disable();
                } else {
                    usageControl.enable();

                    if (!usageControl.value) {
                        usageControl.setValue(5);
                    }
                }
            }
        );

        this.form.controls.perpetual.valueChanges.subscribe(
            perpetual => {
                const expirationControl = this.form.controls.expires_dttm;

                if (perpetual) {
                    expirationControl.setValue('0');
                    expirationControl.disable();
                } else {
                    expirationControl.enable();

                    if (!expirationControl.value || expirationControl.value === '0') {
                        expirationControl.setValue(this.getDefaultExpirationDate());
                    }
                }
            }
        );
    }

    ngOnDestroy(): void {
        this.rows.controls.forEach(row => {row.controls.enroll_code_id.setValue('');});
    }

    get rows(): FormArray<FormGroup> {
        return this.form.controls.rows;
    }

    generateCode(index: number): void {
        const row = this.rows.at(index);
        const length = row.controls.pwMin.value;

        const existingCodes = new Set(
            this.rows.controls
                .filter((_, rowIndex) => rowIndex !== index)
                .map(control => control.controls.enroll_code_id.value)
                .filter((code): code is string => !!code)
        );

        let code: string;

        do {
            code = this.randomCode(length);
        } while (existingCodes.has(code));

        row.controls.enroll_code_id.setValue(code);
    }

    private getDefaultExpirationDate(): Date {
        const expiration = new Date();
        expiration.setFullYear(expiration.getFullYear() + 2);

        return expiration;
    }

    private randomCode(length = this.codeMinimumMin): string {
 
        const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789~!@#$%^&*';
        let retVal: string;
        do {
        retVal = '';
        for (let i = 0, n = charset.length; i < length; ++i) {
            retVal += charset.charAt(Math.floor(this.windowService.cryptoRandom() * n));
        }
        } while (!this.isValidPassword(retVal));

        return retVal;
    }

    isValidPassword(password: string): boolean {
        if (!/\d/.test(password)) {
            return false;
        }
        if (!/[A-Z]/.test(password)) {
            return false;
        }
        if (!/[a-z]/.test(password)) {
            return false;
        }
        if (!/[^ \w]|_/.test(password)) {
            return false;
        }
        return true;
    }

    onCancel() {
        this.dialogRef.close();
    }

    onCreateCodes(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }

        const expiration =
            this.form.controls.expires_dttm.value;

        const result: IEnrollCodesDialogResult = {
            type: this.options.type,
            usage_count:
                this.form.controls.unlimited_usage.value ? -1 : this.form.controls.usage_count.value!,
            expires_dttm:
                this.form.controls.perpetual.value ? '0' : expiration instanceof Date? expiration.toISOString() : expiration!,
            codes: this.rows.getRawValue().map(row => ({
                licenseId: row.licenseId,
                biosSerial: row.biosSerial,
                endpointName: row.endpointName,
                diskSerial: row.diskSerial,
                pwMin: row.pwMin,
                enroll_code_id: row.enroll_code_id,
            })),
        };

        this.dialogRef.close(result);
    }

    expirationValidator(): ValidatorFn {
        return (
            control: AbstractControl
        ): ValidationErrors | null => {
            const value = control.value;

            if (!value) {
                return null;
            }

            const expiration = value instanceof Date
                ? new Date(value)
                : new Date(value);

            const today = new Date();
            today.setHours(0, 0, 0, 0);

            if (isNaN(expiration.getTime())) {
                return {
                    invalidDate: true,
                };
            }

            if (expiration < today) {
                return {
                    expired: true,
                };
            }

            return null;
        };
    }
}